import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast-context';
import { useTargets, useUpsertTarget } from '@/lib/queries';

const FIELDS = [
  ['captaciones', 'Captaciones'],
  ['leads', 'Leads'],
  ['citasGeneradas', 'Citas gen.'],
  ['citasConcretadas', 'Citas concr.'],
  ['visitas', 'Visitas'],
  ['separaciones', 'Separaciones'],
  ['ventas', 'Ventas'],
] as const;
type Field = (typeof FIELDS)[number][0];

/** Metas mensuales por proyecto (roles de gestión). */
export function TargetsDialog({
  companyId,
  month,
  open,
  onOpenChange,
}: {
  companyId: string;
  month: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { data } = useTargets(open ? companyId : undefined, month);
  const upsert = useUpsertTarget();
  const toast = useToast();
  const [rows, setRows] = useState<Record<string, Record<Field, number>>>({});
  useEffect(() => {
    if (data)
      setRows(
        Object.fromEntries(
          data.map((t) => [
            t.projectId,
            {
              captaciones: t.captaciones,
              leads: t.leads,
              citasGeneradas: t.citasGeneradas,
              citasConcretadas: t.citasConcretadas,
              visitas: t.visitas,
              separaciones: t.separaciones,
              ventas: t.ventas,
            },
          ]),
        ),
      );
  }, [data]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      for (const [projectId, values] of Object.entries(rows))
        await upsert.mutateAsync({ projectId, yearMonth: month, ...values });
      toast.success('Metas guardadas');
      onOpenChange(false);
    } catch (err) {
      toast.error('No se pudieron guardar las metas', (err as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Metas de {month}</DialogTitle>
            <DialogDescription>
              Por proyecto. El avance se compara con el % del mes transcurrido.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              {/* Cada casilla ocupa todo el ancho de su columna y el título va centrado encima: así título y casilla coinciden aunque los rótulos midan distinto. */}
              <thead className="text-muted-foreground text-left text-xs uppercase">
                <tr>
                  <th className="py-1 pr-2">Proyecto</th>
                  {FIELDS.map(([k, label]) => (
                    <th key={k} className="whitespace-nowrap px-1 py-1 text-center">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.map((t) => (
                  <tr key={t.projectId} className="border-t">
                    <td className="whitespace-nowrap py-1 pr-2 font-medium">{t.projectName}</td>
                    {FIELDS.map(([k]) => (
                      <td key={k} className="px-1 py-1">
                        <Input
                          type="number"
                          min={0}
                          className="h-8 w-full min-w-[4.5rem] text-right"
                          aria-label={`${t.projectName} ${k}`}
                          value={rows[t.projectId]?.[k] ?? 0}
                          onChange={(e) =>
                            setRows((r) => ({
                              ...r,
                              [t.projectId]: {
                                ...(r[t.projectId] ?? ({} as Record<Field, number>)),
                                [k]: Number(e.target.value),
                              },
                            }))
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              Guardar metas
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
